import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createPatientByStaffService,
  getMyHospitalTenantsService,
} from "../api/services";
import { DatePicker } from "../components/DatePicker";
import { useToast } from "../components/Toast";
import { useRequestError } from "../hooks/useRequestError";
import type { HospitalTenant } from "../types/models";
import {
  buildCreatePatientPayload,
  mapPatientRegistrationApiError,
  validatePatientRegistrationValues,
  type PatientRegistrationValues,
} from "../utils/patientRegistration";

const initialValues: PatientRegistrationValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  government_id: "",
  government_id_type: "",
  hospital_tenant_code: "",
  date_of_birth: "",
  password: "",
  password_confirm: "",
};

export const PatientRegistrationPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<
    Partial<Record<keyof PatientRegistrationValues, string>>
  >({});
  const [tenants, setTenants] = useState<HospitalTenant[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getMyHospitalTenantsService()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setTenants(list);
        if (list.length === 1) {
          setValues((prev) => ({
            ...prev,
            hospital_tenant_code: list[0].code,
          }));
        }
      })
      .catch(handleRequestError);
  }, [handleRequestError]);

  const setField = (key: keyof PatientRegistrationValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validatePatientRegistrationValues(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setSubmitting(true);
    try {
      await createPatientByStaffService(buildCreatePatientPayload(values));
      showToast({
        type: "success",
        title: "Patient registered",
        message: "The patient account was created.",
      });
      navigate("/");
    } catch (error) {
      showToast({
        type: "error",
        title: "Registration failed",
        message: mapPatientRegistrationApiError(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page narrow">
      <header className="page-header">
        <h1>Register patient</h1>
        <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
          Back
        </button>
      </header>
      <form className="panel form-grid" onSubmit={onSubmit}>
        {(
          [
            ["first_name", "First name", "text"],
            ["last_name", "Last name", "text"],
            ["email", "Email", "email"],
            ["phone_number", "Phone", "text"],
            ["government_id", "Government ID", "text"],
            ["password", "Password", "password"],
            ["password_confirm", "Confirm password", "password"],
          ] as const
        ).map(([key, label, type]) => (
          <label key={key} className="field">
            <span>{label}</span>
            <input
              type={type}
              value={values[key]}
              onChange={(e) => setField(key, e.target.value)}
            />
            {errors[key] ? <em className="field-error">{errors[key]}</em> : null}
          </label>
        ))}

        <label className="field">
          <span>Date of birth</span>
          <DatePicker
            value={values.date_of_birth}
            onChange={(next) => setField("date_of_birth", next)}
            placeholder="Select date of birth"
          />
          {errors.date_of_birth ? (
            <em className="field-error">{errors.date_of_birth}</em>
          ) : null}
        </label>

        <label className="field">
          <span>Government ID type</span>
          <select
            value={values.government_id_type}
            onChange={(e) =>
              setField(
                "government_id_type",
                e.target.value as PatientRegistrationValues["government_id_type"]
              )
            }
          >
            <option value="">Select…</option>
            <option value="national_id">National ID</option>
            <option value="iqama">Iqama</option>
          </select>
          {errors.government_id_type ? (
            <em className="field-error">{errors.government_id_type}</em>
          ) : null}
        </label>

        <label className="field">
          <span>Hospital</span>
          <select
            value={values.hospital_tenant_code}
            onChange={(e) => setField("hospital_tenant_code", e.target.value)}
          >
            <option value="">Select…</option>
            {tenants.map((tenant) => (
              <option key={tenant.code} value={tenant.code}>
                {tenant.display_name || tenant.name || tenant.code}
              </option>
            ))}
          </select>
          {errors.hospital_tenant_code ? (
            <em className="field-error">{errors.hospital_tenant_code}</em>
          ) : null}
        </label>

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Registering…" : "Register patient"}
        </button>
      </form>
    </div>
  );
};
